import { createContext, useCallback, useContext, useMemo, useState } from "react"

import type { RemovedExtraService } from "../../form/formTypes"

type RemovedServicesContextValue = Readonly<{
    removedServices: RemovedExtraService[]
    addRemovedServices: (services: RemovedExtraService[]) => void
    clearRemovedServices: (ids: ReadonlyArray<number>) => void
}>

const RemovedServicesContext = createContext<RemovedServicesContextValue | null>(null)

export function RemovedServicesProvider({ children }: Readonly<{ children: React.ReactNode }>): React.ReactElement {
    const [removedServices, setRemovedServices] = useState<RemovedExtraService[]>([])

    const addRemovedServices = useCallback((services: RemovedExtraService[]) => {
        setRemovedServices((prev) => {
            const existingIds = new Set(prev.map((r) => r.id))
            const unique = services.filter((s) => !existingIds.has(s.id))
            return unique.length > 0 ? [...prev, ...unique] : prev
        })
    }, [])

    // Drop ids that are available again (e.g. a delivery fee returns after switching back to
    // Delivery), so the "no longer available" alert and the order-summary hide are reverted.
    const clearRemovedServices = useCallback((ids: ReadonlyArray<number>) => {
        const idSet = new Set(ids)
        setRemovedServices((prev) => {
            const next = prev.filter((r) => !idSet.has(r.id))
            return next.length === prev.length ? prev : next
        })
    }, [])

    const value = useMemo(
        () => ({ removedServices, addRemovedServices, clearRemovedServices }),
        [removedServices, addRemovedServices, clearRemovedServices],
    )

    return <RemovedServicesContext.Provider value={value}>{children}</RemovedServicesContext.Provider>
}

export function useRemovedServices(): RemovedServicesContextValue {
    const ctx = useContext(RemovedServicesContext)
    if (!ctx) throw new Error("useRemovedServices must be used within RemovedServicesProvider")
    return ctx
}
